/**
 * SSR shell rules shared by core and the brands (Vite brands stream the document, see
 * `packages/react-router/entry.server.tsx`; legacy brands render it in one piece, where all of
 * this is a no-op).
 *
 * The response leaves the server when the critical work of the page is done (the shell "gate"):
 * status, redirects, `Cache-Control` and cache tags must be decided before that point.
 */

/**
 * Per-request registry of the critical work still pending (`res.locals._shellGate`, Vite brands
 * only). A suspending hook calls `begin(id)` before the call that may suspend and `end(id)` right
 * after it, in the same render: while the component is suspended its id stays pending.
 */
export interface ShellGate {
	begin(id: string): void;
	end(id: string): void;
}

type ShellResponse = {
	headersSent?: boolean;
	locals?: Record<string, unknown>;
	req?: {originalUrl?: string};
};

/** True once the headers are out (the shell was flushed): status and headers can no longer change. */
export const isShellFlushed = (res: ShellResponse | null | undefined): boolean => !!res?.headersSent;

/** Logged with every header, status, redirect or cache tag set after the shell flush (the SSR suites fail on it). */
export const LATE_HEADER_MESSAGE = 'SSR late header';

/**
 * Logged when a lazy module first rendered after the shell flush has CSS that is not in `<head>`: a streamed section
 * may only use CSS already sent with the shell (the SSR suites fail on it).
 */
export const STREAMED_STYLESHEETS_MESSAGE = 'SSR stylesheets of modules streamed after the shell, not in <head>';

/**
 * Logs a header, status, redirect or cache tag set after the shell flush, which is lost.
 * The SSR test suites fail on this message.
 */
export const reportLateHeader = (res: ShellResponse, what: string) => {
	console.error(`${LATE_HEADER_MESSAGE} (${what}) after the shell flush: ${res.req?.originalUrl}`);

	if (res.locals) {
		res.locals._ssrLateHeaders = ((res.locals._ssrLateHeaders as number | undefined) || 0) + 1;
	}
};
